import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personnalisé pour gérer les tâches d'un utilisateur
 * Fournit toutes les fonctionnalités CRUD pour les tâches
 */
export const useTaches = () => {
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Créer des tâches d'exemple pour la démonstration
  const creerTachesExemple = () => {
    // ✅ Retourner un tableau VIDE - plus de tâches d'exemple
    return [];
  };  // Clé de stockage basée sur l'utilisateur connecté
  const getStorageKey = useCallback(() => {
    const userId = localStorage.getItem('userId') || 'user_1';
    return `taches_${userId}`;
  }, []);

  // Charger les tâches depuis localStorage
  const chargerTaches = useCallback(() => {
    setLoading(true);
    try {
      const tachesSauvegardees = localStorage.getItem(getStorageKey());
      if (tachesSauvegardees) {
        const tachesParses = JSON.parse(tachesSauvegardees);
        // Mettre à jour les statuts automatiquement
        const tachesMisesAJour = tachesParses.map(tache => ({
          ...tache,
          statut: calculerStatut(tache)
        }));
        setTaches(tachesMisesAJour);
        sauvegarderTaches(tachesMisesAJour);
      } else {
        // ✅ Ne plus créer de tâches d'exemple automatiquement
        // Commencer avec un tableau vide
        setTaches([]);
        localStorage.setItem(getStorageKey() + '_initialized', 'true');
      }
    } catch (err) {
      setError('Erreur lors du chargement des tâches');
      console.error('Erreur chargement tâches:', err);
    } finally {
      setLoading(false);
    }
  }, [getStorageKey]);

  // Sauvegarder les tâches dans localStorage
  const sauvegarderTaches = useCallback((nouvelles_taches) => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(nouvelles_taches));
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Erreur sauvegarde tâches:', err);
    }
  }, [getStorageKey]);

  // Calculer le statut automatiquement
  const calculerStatut = (tache) => {
    if (tache.statut === 'termine') return 'termine';
    
    const maintenant = new Date();
    const echeance = new Date(tache.echeance);
    
    if (echeance < maintenant) {
      return 'en_retard';
    }
    
    return tache.statut;
  };

  // Calculer les statistiques
  const calculerStats = useCallback(() => {
    const stats = {
      a_faire: 0,
      en_cours: 0,
      termine: 0,
      en_retard: 0,
      total: taches.length
    };

    taches.forEach(tache => {
      const statutActuel = calculerStatut(tache);
      stats[statutActuel]++;
    });

    return {
      ...stats,
      actives: stats.a_faire + stats.en_cours + stats.en_retard,
      tauxCompletion: stats.total > 0 ? Math.round((stats.termine / stats.total) * 100) : 0,
      enRetardPourcentage: stats.total > 0 ? Math.round((stats.en_retard / stats.total) * 100) : 0
    };
  }, [taches]);

  // Trier les tâches
  const trierTaches = useCallback((taches) => {
    return [...taches].sort((a, b) => {
      // D'abord par statut (terminées à la fin)
      if (a.statut === 'termine' && b.statut !== 'termine') return 1;
      if (a.statut !== 'termine' && b.statut === 'termine') return -1;
      
      // Puis par échéance
      const dateA = new Date(a.echeance);
      const dateB = new Date(b.echeance);
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // Puis par priorité
      const prioriteOrder = { urgent: 3, important: 2, normal: 1 };
      return prioriteOrder[b.priorite] - prioriteOrder[a.priorite];
    });
  }, []);

  // Ajouter une nouvelle tâche
  const ajouterTache = useCallback((nouvelleTache) => {
    const tacheAvecId = {
      id: Date.now(),
      ...nouvelleTache,
      dateCreation: new Date().toISOString()
    };
    
    const nouvellesTaches = [...taches, tacheAvecId];
    setTaches(nouvellesTaches);
    sauvegarderTaches(nouvellesTaches);
    return tacheAvecId;
  }, [taches, sauvegarderTaches]);

  // Modifier une tâche existante
  const modifierTache = useCallback((id, modifications) => {
    const nouvellesTaches = taches.map(tache => 
      tache.id === id ? { ...tache, ...modifications } : tache
    );
    setTaches(nouvellesTaches);
    sauvegarderTaches(nouvellesTaches);
  }, [taches, sauvegarderTaches]);

  // Supprimer une tâche
  const supprimerTache = useCallback((id) => {
    const nouvellesTaches = taches.filter(tache => tache.id !== id);
    setTaches(nouvellesTaches);
    sauvegarderTaches(nouvellesTaches);
  }, [taches, sauvegarderTaches]);

  // Basculer le statut terminé/non terminé
  const toggleStatutTache = useCallback((id) => {
    const nouvellesTaches = taches.map(tache => {
      if (tache.id === id) {
        const nouveauStatut = tache.statut === 'termine' ? 'a_faire' : 'termine';
        return { ...tache, statut: nouveauStatut };
      }
      return tache;
    });
    setTaches(nouvellesTaches);
    sauvegarderTaches(nouvellesTaches);
  }, [taches, sauvegarderTaches]);

  // Nettoyer les tâches anciennes
  const nettoyerTachesAnciennes = useCallback((joursLimit = 30) => {
    const maintenant = new Date();
    const dateLimit = new Date(maintenant.getTime() - (joursLimit * 24 * 60 * 60 * 1000));
    
    const tachesFiltered = taches.filter(tache => {
      if (tache.statut !== 'termine') return true;
      const dateCreation = new Date(tache.dateCreation);
      return dateCreation > dateLimit;
    });

    const nbSupprimees = taches.length - tachesFiltered.length;
    
    if (nbSupprimees > 0) {
      setTaches(tachesFiltered);
      sauvegarderTaches(tachesFiltered);
    }
    
    return nbSupprimees;
  }, [taches, sauvegarderTaches]);

  // Obtenir les tâches par statut
  const getTachesParStatut = useCallback((statut) => {
    return taches.filter(tache => calculerStatut(tache) === statut);
  }, [taches]);

  // Obtenir les tâches par priorité
  const getTachesParPriorite = useCallback((priorite) => {
    return taches.filter(tache => tache.priorite === priorite);
  }, [taches]);

  // Obtenir les tâches par catégorie
  const getTachesParCategorie = useCallback((categorie) => {
    return taches.filter(tache => tache.categorie === categorie);
  }, [taches]);

  // Rechercher dans les tâches
  const rechercherTaches = useCallback((terme) => {
    const termeMinuscule = terme.toLowerCase();
    return taches.filter(tache => 
      tache.titre.toLowerCase().includes(termeMinuscule) ||
      (tache.description && tache.description.toLowerCase().includes(termeMinuscule))
    );
  }, [taches]);

  // Exporter les tâches
  const exporterTaches = useCallback((format = 'json') => {
    if (format === 'json') {
      const data = JSON.stringify(taches, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `mes-taches-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [taches]);

  // Importer des tâches
  const importerTaches = useCallback((fichier) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const tachesImportees = JSON.parse(e.target.result);
          if (Array.isArray(tachesImportees)) {
            const nouvellesTaches = [...taches, ...tachesImportees.map(t => ({
              ...t,
              id: Date.now() + Math.random(),
              dateCreation: t.dateCreation || new Date().toISOString()
            }))];
            setTaches(nouvellesTaches);
            sauvegarderTaches(nouvellesTaches);
            resolve(tachesImportees.length);
          } else {
            reject(new Error('Format de fichier invalide'));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Erreur de lecture du fichier'));
      reader.readAsText(fichier);
    });
  }, [taches, sauvegarderTaches]);

  // Charger les tâches au montage
  useEffect(() => {
    chargerTaches();
  }, [chargerTaches]);

  // Calculer automatiquement les stats
  const stats = calculerStats();
  const tachesTriees = trierTaches(taches);

  // Réinitialiser avec des tâches d'exemple (pour démo/test)
  // ✅ DÉSACTIVÉ - Ne plus créer de tâches d'exemple
  const reinitialiserAvecExemples = useCallback(() => {
    // Vider toutes les tâches au lieu de créer des exemples
    setTaches([]);
    sauvegarderTaches([]);
    localStorage.setItem(getStorageKey() + '_initialized', 'true');
  }, [getStorageKey, sauvegarderTaches]);

  // Vider toutes les tâches (reset complet)
  const viderToutesLesTaches = useCallback(() => {
    setTaches([]);
    localStorage.removeItem(getStorageKey());
    localStorage.removeItem(getStorageKey() + '_initialized');
  }, [getStorageKey]);

  return {
    // État
    taches: tachesTriees,
    loading,
    error,
    stats,
    
    // Actions CRUD
    ajouterTache,
    modifierTache,
    supprimerTache,
    toggleStatutTache,
    
    // Utilitaires
    chargerTaches,
    nettoyerTachesAnciennes,
    getTachesParStatut,
    getTachesParPriorite,
    getTachesParCategorie,
    rechercherTaches,
    exporterTaches,
    importerTaches,
    reinitialiserAvecExemples,
    viderToutesLesTaches,
    
    // Helpers
    calculerStatut,
    trierTaches
  };
};

export default useTaches;
